import React from 'react';
import PageContainer from 'components/PageContainer';
import PageContent from 'components/PageContent';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import InteractiveTable, { Column } from 'components/InteractiveTable';
import { dateToLabel } from 'further-ui/utils';
import {
  createPartnerTransfersRoute,
  createPartnerTransfersCreateRoute,
} from 'adminConstants/routes';
import { Edit, Delete, Add } from '@mui/icons-material';
import TableActions from 'components/Table/TableActions';
import ErrorBoundary from 'components/ErrorBoundary';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { Button } from '@mui/material';
import { ActionStatus } from 'further-types/lp/action';

const pageId = 'partner-transfers';

const PartnerTransfersListContent: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const [partnerTransfersData, query] =
    api.partnerTransfers.list.useSuspenseQuery({
      vehicleId: vehicle?._id ?? '',
    });

  const mutationOpts = useHandleTrpcMutation<{
    success: boolean;
  }>();

  const deletePartnerTransfer = api.partnerTransfers.delete.useMutation(
    mutationOpts({
      successMessage: 'Partner transfer deleted successfully',
    }),
  );

  const partnerTransfers = partnerTransfersData?.results ?? [];

  const columns: Array<Column> = [
    {
      field: 'transferDate',
      headerName: 'Transfer date',
      flex: 1,
      renderCell: ({ row }) => dateToLabel(row.transferDate),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: ({ row }) =>
        row.status === ActionStatus.Draft ? 'Draft' : 'Approved',
    },
    {
      field: 'transferees',
      headerName: 'Transferees',
      flex: 1,
      renderCell: ({ row }) => row.transferees.length,
    },
    {
      headerName: 'Actions',
      sortable: false,
      field: 'actions',
      renderCell: ({ row }) => (
        <TableActions
          size="small"
          actions={[
            {
              label: 'Edit',
              icon: Edit,
              color: 'primary',
              link: createPartnerTransfersRoute(row.id ?? ''),
              disabled: !row.isDraft,
            },
            {
              label: 'Delete',
              icon: Delete,
              color: 'error',
              onClick: () =>
                deletePartnerTransfer.mutate({
                  partnerTransferId: row.id ?? '',
                  vehicleId: vehicle?._id ?? '',
                }),
              disabled: !row.canBeDeleted || deletePartnerTransfer.isPending,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <InteractiveTable
      fullHeight
      columns={columns}
      id={`${pageId}-table`}
      disableTableConfiguration
      loading={query.isFetching}
      rows={partnerTransfers}
      rowCount={partnerTransfers.length ?? 0}
    />
  );
};

const ListPartnerTransfers: React.FC = () => {
  return (
    <PageContainer
      heading="Partner Transfers"
      subheaderComponent={
        <Button
          variant="contained"
          startIcon={<Add />}
          href={createPartnerTransfersCreateRoute()}
        >
          Create Partner Transfer
        </Button>
      }
    >
      <PageContent>
        <ErrorBoundary>
          <PartnerTransfersListContent />
        </ErrorBoundary>
      </PageContent>
    </PageContainer>
  );
};

export default ListPartnerTransfers;
